import {
  ApplicationElement,
  html,
  render,
} from '../application_element/element.js';
import { ref, createRef } from 'lit/directives/ref.js';
import { chatConfig } from '../chat/lib/config.js';
import style from './style.lit.css';
import { todayOrYesterday } from '../../lib/helpers.js';

import '../scroller/element.js';
import { IntersectionController } from '@lit-labs/observers/intersection-controller.js';
import translations from './translations.js';
import { repeat } from 'lit/directives/repeat.js';

import '../chat_message_new/element.js';

/**
 * Chat form - used for sending chat messages
 *
 */
export default class ChatMessagesList extends ApplicationElement {
  /** @internal */
  static translations = translations;

  static styles = [ApplicationElement.styles, style];

  static properties = {
    chat: { attribute: false },
    messages: { attribute: false },
    noMoreMessages: { type: Boolean },
    firstNewMessage: { attribute: false },
    newMessagesCount: { type: Number, attribute: false },
    loading: { type: Boolean },
    showScrollIndicator: { type: Boolean },
    messageForEdit: { type: Object, attribute: false },
    scrolling: { type: Boolean, reflect: true },
    unreadMessagesToLoadCount: { type: Number },
    adminIds: { type: Array, attribute: false },
  };

  scrollElementRef = createRef();

  constructor() {
    super();
    this.createNewMessagesSeparator();
    this.today = new Date();
    this.adminIds = [];
  }

  connectedCallback() {
    super.connectedCallback();
    this.forcedScrollToEnd = true;
  }

  willUpdate(prevChanges) {
    super.willUpdate();

    this.setupNewMessagesIntersector(prevChanges);
    this.saveScrollPositionIfFirstItemChanges(prevChanges);
  }

  firstUpdated() {
    this.initIntersectionObserver();
  }

  async updated(prevChanges) {
    // We need to wait for previous scrolls to be finished
    await this.resolveScrollPositionOnUpdatedPromise;

    this.resolveScrollPositionOnUpdatedPromise =
      this.resolveScrollPositionOnUpdated(prevChanges);
  }

  get scrollElement() {
    return this.scrollElementRef.value;
  }

  // Delegate to scrollElement
  get scrollNearBottom() {
    return this.scrollElement.scrollNearBottom;
  }

  observerCallback(entry) {
    const { rootBounds, boundingClientRect } = entry;
    const position =
      rootBounds.bottom - boundingClientRect.bottom + boundingClientRect.height;

    this.newMessagesSeparatorPosition = position;
    this.newMessagesSeparatorVisible = entry.isIntersecting;
    this.dispatchEvent(new Event('new-messages-separator-intersected'));
  }

  initIntersectionObserver() {
    this.newMessagesIntersector = new IntersectionController(this, {
      target: null,
      callback: ([entry]) => this.observerCallback(entry),
      config: {
        root: this.scrollElementRef.value,
      },
    });
  }

  createNewMessagesSeparator() {
    const el = document.createElement('div');
    el.className = 'separator message-unread-separator';
    this.newMessagesSeparator = el;
  }

  scrollToEnd() {
    return this.scrollElement.scrollToEnd();
  }

  scrollToTop() {
    return this.scrollElement.scrollToTop('smooth');
  }

  scrollToNewMessageSeparator() {
    this.newMessagesSeparator.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    });
  }

  // saveScrollPosition only if first item changes
  // which means we are probably prepending items
  // which means we are doing load items to the top
  saveScrollPositionIfFirstItemChanges(prevChanges) {
    if (!prevChanges.has('messages')) return;
    if (prevChanges.get('messages')?.[0] === this.messages[0]) return;

    this.scrollElement?.saveScrollPosition();
  }

  setupNewMessagesIntersector(prevChanges) {
    if (!prevChanges.has('firstNewMessage')) return;
    const { firstNewMessage, newMessagesCount, newMessagesIntersector } = this;
    const prevFirstNewMessage = prevChanges.get('firstNewMessage');

    if (
      prevFirstNewMessage?.uuid !== firstNewMessage?.uuid &&
      newMessagesCount
    ) {
      // This will just re-observe existing target
      newMessagesIntersector?.observe(this.newMessagesSeparator);
    }
  }

  skipResolveScrollPositionOnUpdated(prevChanges) {
    return (
      !this.isConnected ||
      !prevChanges.has('messages') ||
      !this.messages.length ||
      this.loading
    );
  }

  async resolveScrollPositionOnUpdated(prevChanges) {
    const { forcedScrollToEnd, scrollElement, messages } = this;

    if (this.skipResolveScrollPositionOnUpdated(prevChanges)) return;

    // Reset flag
    this.forcedScrollToEnd = false;

    const prevMessages = prevChanges.get('messages');

    if (forcedScrollToEnd) {
      return await scrollElement.scrollToEnd();
    }

    if (messages?.length !== prevMessages?.length) {
      return (
        (await scrollElement.scrollToEndIfNearEnd()) ||
        (await scrollElement.restoreScrollPosition())
      );
    }
  }

  onNewMessageScrollIndicatorClick() {
    const indicator = this.chat.listRef.value;
    indicator.scrollToNewMessageSeparator();
    this.scrollIndicatorVisible = false;
  }

  onMarkAllAsRead() {
    this.chat.markChatAsRead();
  }

  getSystemMessage({ actorName, subjectName, systemMsgAction }) {
    if (this.chat.currentUser.name === actorName) {
      return this.t(`system_message.${systemMsgAction}.message_actor`, {
        subject_name: subjectName,
      });
    }

    if (this.chat.currentUser.name === subjectName) {
      if (systemMsgAction === 'add_user' || systemMsgAction === 'remove_user') {
        return this.t(`system_message.${systemMsgAction}.message_subject`, {
          actor_name: actorName,
        });
      }
    }

    return this.t(`system_message.${systemMsgAction}.message`, {
      actor_name: actorName,
      subject_name: subjectName,
    });
  }

  handleScroll() {
    clearTimeout(this.timeout);
    this.scrolling = true;

    this.timeout = setTimeout(() => {
      this.scrolling = false;
    }, 1000);
  }

  renderLoading() {
    return html`<div class="loader">
      <ef-icon name=${chatConfig.icons.loader}></ef-icon>
    </div>`;
  }

  renderNewMessageScrollIndicator() {
    return html`
      <button
        @click=${this.onNewMessageScrollIndicatorClick}
        class="new-messages-indicator">
        ${this.t('new_messages_indicator')}
        <ef-icon name=${chatConfig.icons.new_message_indicator}></ef-icon>
      </button>
    `;
  }

  renderUnreadMessagesIndicator() {
    return html`
      <div class="unread-messages-indicator">
        <div>
          <ef-icon name=${chatConfig.icons.new_message_indicator_up}></ef-icon>
          ${this.t('unread_messages_separator_up', {
            count: this.unreadMessagesToLoadCount,
          })}
        </div>

        <button @click=${this.onMarkAllAsRead}>
          <ef-icon name=${chatConfig.icons.mark_all_as_read}></ef-icon>
          ${this.t('mark_all_as_read')}
        </button>
      </div>
    `;
  }

  renderMessage(message) {
    const { chat } = this;
    const my = message.userId === chat.currentUser.id;

    const parentMessage = chat?.getMessage?.(message.parent?.uuid);

    const isCurrentUserAdmin = this.adminIds.includes(chat.currentUser.id);

    return html`
      <ef-chat-message-new
        tabIndex="0"
        .chatList=${this}
        .uuid=${message.uuid}
        .isCurrentUserAdmin=${isCurrentUserAdmin}
        .isRepresentative=${message.isUserOfficial}
        .userName=${message.userName}
        .userPicture=${message.userPicture}
        .userUrl=${message.userUrl}
        .userId=${message.userId}
        .uploading=${message.uploading}
        .attachment=${message.attachment}
        .readIds=${message.readIds}
        .unsent=${message.unsent}
        .uploadProgress=${message.uploadProgress}
        .images=${message.images}
        .imageWidth=${message.imageWidth}
        .imageHeight=${message.imageHeight}
        .editMode=${message.uuid === this.messageForEdit?.uuid}
        .my=${my}
        .errors=${message.errors}
        .savedAt=${message.savedAt}
        .createdAt=${message.createdAt}
        .editedAt=${message.editedAt}
        .deletedAt=${message.deletedAt}
        .deletedByUserName=${message.deletedByUserName}
        .receivedAt=${message.receivedAt}
        .currentUser=${chat.currentUser}
        .chatMembersCount=${chat.membersCount}
        .body=${message.body}
        .showUserName=${chat.showUserName}
        .parentMessage=${parentMessage}>
      </ef-chat-message-new>
    `;
  }

  renderNoMoreMessages() {
    return html`<div class="no-more-messages">
      ${this.t('no_more_messages')}
    </div>`;
  }

  renderSystemMessage(message) {
    return html`<div class="message-system">
      <span>${this.getSystemMessage(message)}</span>
    </div>`;
  }

  renderMessageDateSeparator(date) {
    let content;

    const toy = todayOrYesterday(date, this.today);

    if (toy === 0) {
      content = this.t('today');
    } else if (toy === 1) {
      content = this.t('yesterday');
    } else {
      content = this.i18n.l('date.formats.default', date);
    }

    return html`<div class="separator message-date-separator">
      <span>${content}</span>
    </div>`;
  }

  renderNewMessagesSeparator() {
    const content = this.t('unread_messages_separator', {
      count: this.newMessagesCount,
    });

    render(content, this.newMessagesSeparator);
    return this.newMessagesSeparator;
  }

  renderMessages() {
    if (!this.messages) return [];

    const list = [];
    let lastDay;

    const { messages, firstNewMessage } = this;

    messages.forEach(message => {
      const currentDate = new Date(message.createdAt);
      const currentDay = currentDate.getDate();

      if (lastDay !== currentDay) {
        list.push({
          id: `separator-${currentDate}`,
          value: this.renderMessageDateSeparator(currentDate),
        });
      }

      if (firstNewMessage?.uuid === message.uuid) {
        list.push({ value: this.renderNewMessagesSeparator() });
      }

      if (message.kind === 'system') {
        list.push({
          id: message.uuid,
          value: this.renderSystemMessage(message),
        });
      } else {
        list.push({ id: message.uuid, value: this.renderMessage(message) });
      }

      // For next loop
      lastDay = currentDay;
    });

    return list;
  }

  render() {
    return html`
      ${this.loading ? this.renderLoading() : null}
      ${this.unreadMessagesToLoadCount
        ? this.renderUnreadMessagesIndicator()
        : null}
      ${this.showScrollIndicator
        ? this.renderNewMessageScrollIndicator()
        : null}

      <ef-scroller
        @scroller-active=${this.handleScroll}
        .disableNearTopDetector=${this.loading}
        ${ref(this.scrollElementRef)}>
        <div class="main">
          ${this.noMoreMessages ? this.renderNoMoreMessages() : null}
          ${repeat(
            this.renderMessages(),
            item => item.id,
            item => item.value
          )}
        </div>
      </ef-scroller>
    `;
  }
}

ChatMessagesList.registerTagName('ef-chat-messages-list');

customElements.get('ef-chat-messages-list') ||
  customElements.define('ef-chat-messages-list', ChatMessagesList);
